import { useRef, useState } from "react";
import api from "@/lib/api";
import { Paperclip, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * PhotoUploader — used inside assessment dialogs.
 * props:
 *  - value: string[] of file IDs already attached
 *  - onChange: (string[]) => void
 *  - testid: prefix for data-testids
 */
export default function PhotoUploader({ value = [], onChange, testid = "photo" }) {
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({}); // id -> filename
  const inputRef = useRef();

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("category", "assessment");
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMeta((m) => ({ ...m, [data.id]: data.original_filename }));
      onChange([...(value || []), data.id]);
      toast.success("Photo attached");
    } catch {
      toast.error("Upload failed");
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => onChange((value || []).filter((x) => x !== id));

  return (
    <div className="border border-dashed border-slate-300 rounded-md p-2 bg-slate-50/50">
      {(value || []).length > 0 && (
        <ul className="space-y-1 mb-2">
          {value.map((id) => (
            <li key={id} data-testid={`${testid}-item-${id}`} className="flex items-center gap-2 px-2 py-1 bg-white border border-slate-200 rounded-md text-xs">
              <ImageIcon className="h-3.5 w-3.5 text-slate-500" />
              <span className="flex-1 truncate text-slate-700">{meta[id] || `Photo ${id.slice(0, 8)}`}</span>
              <button type="button" onClick={() => remove(id)} data-testid={`${testid}-remove-${id}`} className="text-slate-400 hover:text-rose-600">
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900">
        <input ref={inputRef} type="file" hidden accept="image/*,.pdf" onChange={pick} data-testid={`${testid}-input`} />
        <Paperclip className="h-3.5 w-3.5" /> {busy ? "Uploading…" : "Attach photo or PDF"}
      </label>
    </div>
  );
}
